import { lazy, Suspense, useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ensureCapabilities } from '@/lib/capabilities-api';
import { useMutation } from '@tanstack/react-query';
import { useContactPhone } from '@/lib/contact';

import { ridesApi } from '@/lib/rides-api';
import { customerName } from '@/lib/customer';
import { rideKeys } from '@/lib/query-keys';
import { useBeckonDraft } from '@/lib/use-beckon-draft';
import { formatMoney } from '@/lib/format';
import { vehicleLabel } from '@/lib/ride-format';
import type { RideOption } from '@/lib/rides-types';
import type { RideSearchParams } from '@/components/rides/ride-map-picker';
import { RideOptionCard } from '@/components/rides/ride-option-card';
import { Skeleton } from '@/components/shared/skeleton';
import { Screen, AccountButton } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { SumRow } from '@/components/ui/sum-row';
import { ProgressLabel } from '@/components/ui/progress-label';

// Lazy-load the map picker — mapbox-gl is ~550KB and only needed on the rides routes.
const RideMapPicker = lazy(() =>
  import('@/components/rides/ride-map-picker').then((m) => ({ default: m.RideMapPicker }))
);

const DEFAULT_CITY = 'std:080'; // Bangalore — required by the ONDC gateway (no backend default).

export const Route = createFileRoute('/_authed/rides/')({
  // Rides (TRV10) hard-503 when the domain is off — redirect home rather than
  // dead-end. Fails closed (ensureCapabilities returns all-off on error).
  beforeLoad: async () => {
    const caps = await ensureCapabilities();
    if (!caps.rides) throw redirect({ to: '/' });
  },
  component: RideBookingPage,
});

function RideBookingPage() {
  const navigate = useNavigate();
  // Google sign-in supplies no phone; the operator-set booking contact does. See lib/contact.ts.
  const [phone] = useContactPhone();

  const [txnId, setTxnId] = useState<string | null>(null);
  const [selected, setSelected] = useState<RideOption | null>(null);
  const [riderName, setRiderName] = useState('');

  const { draft, timedOut, refetch } = useBeckonDraft(txnId, ridesApi.draft, rideKeys.draft);

  const searchMut = useMutation({
    mutationFn: ridesApi.search,
    onSuccess: (ack) => setTxnId(ack.transactionId),
  });
  // Each action reuses the same txn; resume polling on success to pick up the next callback.
  const selectMut = useMutation({ mutationFn: ridesApi.select, onSuccess: () => void refetch() });
  const initMut = useMutation({ mutationFn: ridesApi.init, onSuccess: () => void refetch() });
  const confirmMut = useMutation({ mutationFn: ridesApi.confirm, onSuccess: () => void refetch() });

  // On confirm, the order id lands — go to the live-ride screen. The wizard resumes
  // polling after each action because the backend persists the *_INITIATED status
  // (→ step pending) synchronously before acking. If the draft reads confirm/completed
  // but the order id hasn't been written yet (a brief non-atomic window when the order
  // is created just after the txn flips), keep polling until it appears rather than stalling.
  useEffect(() => {
    if (draft?.step !== 'confirm' || draft.status !== 'completed') return;
    if (draft.orderId) {
      void navigate({ to: '/rides/$id', params: { id: draft.orderId } });
      return;
    }
    const t = setTimeout(() => void refetch(), 800);
    return () => clearTimeout(t);
  }, [draft, navigate, refetch]);

  function reset() {
    setTxnId(null);
    setSelected(null);
  }

  function handleBack() {
    if (txnId) reset();
    else void navigate({ to: '/' });
  }

  function handleSearch(params: RideSearchParams) {
    reset();
    searchMut.mutate({
      cityCode: DEFAULT_CITY,
      pickupGps: params.pickupGps,
      dropGps: params.dropGps,
      vehicleCategory: params.vehicleCategory,
      agentGender: params.agentGender,
    });
  }

  function handleSelect() {
    if (!txnId || !selected) return;
    selectMut.mutate({
      transactionId: txnId,
      providerId: selected.providerId,
      bppId: selected.bppId,
      bppUri: selected.bppUri,
      itemId: selected.itemId,
      fulfillmentId: selected.fulfillmentId,
    });
  }

  function handleGetFare() {
    if (!txnId || !selected) return;
    initMut.mutate({
      transactionId: txnId,
      providerId: selected.providerId,
      itemId: selected.itemId,
      fulfillmentId: selected.fulfillmentId,
      billingName: riderName.trim() || customerName() || 'Rider',
      billingPhone: phone,
    });
  }

  function handleConfirm() {
    if (!txnId || !selected) return;
    confirmMut.mutate({
      transactionId: txnId,
      providerId: selected.providerId,
      itemId: selected.itemId,
      fulfillmentId: selected.fulfillmentId,
      billingName: riderName.trim() || customerName() || 'Rider',
      billingPhone: phone,
    });
  }

  const actionPending =
    searchMut.isPending || selectMut.isPending || initMut.isPending || confirmMut.isPending;

  const showOptions = draft?.step === 'search' && draft.status === 'completed';
  const showRider = draft?.step === 'select' && draft.status === 'completed';
  const showReview = draft?.step === 'init' && draft.status === 'completed';
  const polling =
    !!txnId && draft?.status !== 'completed' && draft?.status !== 'error' && !timedOut;
  const draftErrored = draft?.status === 'error';

  const fareTotal = draft?.quote?.total ?? selected?.fare;
  const stepIndex = showReview ? 3 : showRider ? 2 : showOptions ? 1 : 0;

  return (
    <Screen
      title="Book a ride"
      accent="ride"
      onBack={handleBack}
      right={<AccountButton />}
      steps={{ total: 4, current: stepIndex }}
    >
      <div className="wb-col wb-gap-16">
        {!txnId && (
          <Suspense fallback={<Skeleton w="100%" h={320} r={22} />}>
            <RideMapPicker onFindRides={handleSearch} pending={searchMut.isPending} />
          </Suspense>
        )}

        {polling && <ProgressLabel label={stepLabel(draft?.step)} />}

        {timedOut && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{ color: 'var(--honey-700)', background: 'var(--honey-wash-2)' }}
          >
            {draft?.step === 'confirm'
              ? 'Booking confirmed — finding you a driver. This can take a moment.'
              : 'Taking longer than usual.'}{' '}
            <button
              type="button"
              onClick={() => void refetch()}
              className="wb-strong"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {draft?.step === 'confirm' ? 'Check now' : 'Retry'}
            </button>
          </div>
        )}

        {draftErrored && draft?.error && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-8 wb-small"
            style={{
              color: 'var(--danger)',
              background: 'color-mix(in oklab, var(--danger) 10%, var(--surface))',
              borderColor: 'color-mix(in oklab, var(--danger) 30%, transparent)',
            }}
          >
            <p>{draft.error.message}</p>
            <div>
              <Button variant="secondary" size="sm" onClick={reset}>
                Start over
              </Button>
            </div>
          </div>
        )}

        {/* Ride options */}
        {showOptions && (
          <div className="wb-col wb-gap-12">
            {(draft?.options ?? []).map((opt) => (
              <RideOptionCard
                key={`${opt.providerId}:${opt.itemId}`}
                option={opt}
                selected={
                  selected?.itemId === opt.itemId && selected?.providerId === opt.providerId
                }
                onSelect={() => setSelected(opt)}
              />
            ))}
            {(draft?.options ?? []).length === 0 && (
              <div className="wb-card wb-card--pad wb-col wb-gap-8">
                <p className="wb-strong">No rides available right now.</p>
                <p className="wb-small">
                  There are no drivers near your pickup at the moment. Try a different pickup or
                  drop-off point, or search again in a few minutes.
                </p>
                <div>
                  <Button variant="secondary" size="sm" onClick={reset}>
                    Search again
                  </Button>
                </div>
              </div>
            )}
            {selected && (
              <Button variant="primary" block onClick={handleSelect} disabled={actionPending}>
                Continue
              </Button>
            )}
          </div>
        )}

        {/* Rider details (after select) */}
        {showRider && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-14">
            <h2 className="wb-h2">Rider details</h2>
            {draft?.quote && (
              <p className="wb-sub">
                Estimated fare:{' '}
                <span className="wb-strong" style={{ color: 'var(--ink)' }}>
                  {formatMoney(draft.quote.total)}
                </span>
              </p>
            )}
            <Field
              label="Name"
              value={riderName}
              onChange={(e) => setRiderName(e.target.value)}
              placeholder="Full name"
              hint="Shown to your driver so they can identify you at pickup."
            />
            <p className="wb-tiny">Trip updates sent to {phone}</p>
            <Button variant="primary" block onClick={handleGetFare} disabled={actionPending}>
              Continue
            </Button>
          </div>
        )}

        {/* Review + Confirm (after init) — no payment step; the driver collects on fulfillment. */}
        {showReview && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <h2 className="wb-h2">Review</h2>
            <div>
              <SumRow label="Vehicle">
                {vehicleLabel(selected.vehicleCategory, selected.vehicleVariant)}
              </SumRow>
              {selected.etaMinutes && (
                <SumRow label="Pickup ETA">{`${selected.etaMinutes} min`}</SumRow>
              )}
              <hr className="wb-divider" style={{ margin: '6px 0' }} />
              <SumRow label="Fare" big>
                {formatMoney(fareTotal)}
              </SumRow>
            </div>
            <div
              className="wb-small"
              style={{
                background: 'color-mix(in oklab, var(--success) 12%, var(--surface))',
                color: 'var(--success)',
                borderRadius: 'var(--r-md)',
                padding: '12px 14px',
              }}
            >
              No payment is taken now. You&apos;ll pay the driver {formatMoney(fareTotal)} directly
              at drop-off — cash or UPI.
            </div>
            <Button variant="primary" block onClick={handleConfirm} disabled={confirmMut.isPending}>
              {confirmMut.isPending ? 'Booking…' : 'Confirm ride'}
            </Button>
          </div>
        )}
      </div>
    </Screen>
  );
}

function stepLabel(step?: string): string {
  return step === 'select'
    ? 'Calculating your fare…'
    : step === 'init'
      ? 'Confirming your fare…'
      : step === 'confirm'
        ? 'Booking your ride…'
        : 'Searching for nearby rides…';
}
